import {
  Box,
  Text,
  Divider,
  Grid,
  Link,
  Flex,
  Button,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  useState,
  useCallback,
  useContext,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import moment from 'moment';
import PaymentMethodsList from './PaymentMethodsList';
import { useBilling } from '../../../store/client/BillingContext';
import UpdateBillingContactModal from './UpdateBillingContactModal';
import AddPaymentMethodModal from './AddPaymentMethodModal';
import SetDefaultPaymentMethodModal from './SetDefaultPaymentMethodModal';
import RemovePaymentMethodModal from './RemovePaymentMethodModal';
import TaxIdEditModal from './TaxIdEditModal';
import { WarningIcon } from '@chakra-ui/icons';
import { HttpContext } from '../../../context/HttpContext';
import BillingHistoryTable from './BillingHistoryTable';
import CustomToast from '../../../common/CustomToast';
import TaxIdDisplay from './TaxIdDisplay';

const BillingAndPayment = ({ formatPrice, subscription }) => {
  const {
    customer,
    paymentMethods,
    taxId,
    isLoading,
    createSetupIntent,
    attachPaymentMethod,
    updateBillingDetails,
    updateTaxId,
    removeTaxId,
    setDefaultPaymentMethod,
    removePaymentMethod,
    refresh,
  } = useBilling();

  const [invoices, setInvoices] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  // Modal controls
  const {
    isOpen: isContactModalOpen,
    onOpen: onContactModalOpen,
    onClose: onContactModalClose,
  } = useDisclosure();

  const {
    isOpen: isPaymentModalOpen,
    onOpen: onPaymentModalOpen,
    onClose: onPaymentModalClose,
  } = useDisclosure();

  const {
    isOpen: isSetDefaultModalOpen,
    onOpen: onSetDefaultModalOpen,
    onClose: onSetDefaultModalClose,
  } = useDisclosure();

  const {
    isOpen: isRemoveModalOpen,
    onOpen: onRemoveModalOpen,
    onClose: onRemoveModalClose,
  } = useDisclosure();

  const {
    isOpen: isTaxIdModalOpen,
    onOpen: onTaxIdModalOpen,
    onClose: onTaxIdModalClose,
  } = useDisclosure();

  const [isUpdatingPayment, setIsUpdatingPayment] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [paymentMethodError, setPaymentMethodError] = useState(null);
  const { authAxios } = useContext(HttpContext);
  const fetchIdRef = useRef(0);
  const authAxiosRef = useRef(authAxios);

  // Handle adding a new payment method
  const handleAddPaymentMethod = useCallback(async () => {
    setPaymentMethodError(null);
    onPaymentModalOpen();
  }, [onPaymentModalOpen]);

  // Handle opening the set default confirmation modal
  const handleSetDefaultConfirm = useCallback(
    (paymentMethodId) => {
      const method = paymentMethods.find((pm) => pm.id === paymentMethodId);
      if (method) {
        setSelectedPaymentMethod(method);
        onSetDefaultModalOpen();
      }
    },
    [paymentMethods, onSetDefaultModalOpen]
  );

  // Handle opening the remove payment method confirmation modal
  const handleRemoveConfirm = useCallback(
    (paymentMethodId) => {
      const method = paymentMethods.find((pm) => pm.id === paymentMethodId);
      if (method) {
        setSelectedPaymentMethod(method);
        onRemoveModalOpen();
      }
    },
    [paymentMethods, onRemoveModalOpen]
  );

  // Handle setting a payment method as default
  const handleSetDefaultPaymentMethod = useCallback(
    async (paymentMethodId) => {
      try {
        setIsUpdatingPayment(true);
        await setDefaultPaymentMethod(paymentMethodId);
        toast({
          render: (props) => (
            <CustomToast
              status="success"
              title="Payment method set as default"
              description=""
              onClose={props.onClose}
              id={props.id}
            />
          ),
        });
        onSetDefaultModalClose();
      } catch (error) {
        console.error('Error setting default payment method:', error);
        setPaymentMethodError('Failed to set default payment method');
      } finally {
        setIsUpdatingPayment(false);
      }
    },
    [setDefaultPaymentMethod, onSetDefaultModalClose]
  );

  // Handle removing a payment method
  const handleRemovePaymentMethod = useCallback(
    async (paymentMethodId) => {
      try {
        setIsUpdatingPayment(true);
        await removePaymentMethod(paymentMethodId);
        toast({
          render: (props) => (
            <CustomToast
              status="success"
              title="Payment method removed"
              description=""
              onClose={props.onClose}
              id={props.id}
            />
          ),
        });
        onRemoveModalClose();
      } catch (error) {
        console.error('Error removing payment method:', error);
        setPaymentMethodError('Failed to remove payment method');
      } finally {
        setIsUpdatingPayment(false);
      }
    },
    [removePaymentMethod, onRemoveModalClose]
  );

  // Submit payment method from the modal
  const handlePaymentSubmit = useCallback(
    async ({ paymentMethodId }) => {
      try {
        setIsUpdatingPayment(true);
        await attachPaymentMethod(paymentMethodId);
        toast({
          render: (props) => (
            <CustomToast
              status="success"
              title="Payment method added"
              description=""
              onClose={props.onClose}
              id={props.id}
            />
          ),
        });
        onPaymentModalClose();
      } catch (error) {
        console.error('Error adding payment method:', error);
        setPaymentMethodError('Failed to add payment method');
      } finally {
        setIsUpdatingPayment(false);
      }
    },
    [attachPaymentMethod, onPaymentModalClose]
  );

  const handleUpdateBillingContact = useCallback(
    async (billingDetails) => {
      await updateBillingDetails(billingDetails);
      refresh();
    },
    [updateBillingDetails, refresh]
  );

  const handleTaxIdEdit = useCallback(() => {
    onTaxIdModalOpen();
  }, [onTaxIdModalOpen]);

  const handleTaxIdUpdate = useCallback(
    async (taxIdType, taxIdValue) => {
      updateTaxId(taxIdType, taxIdValue);
    },
    [updateTaxId]
  );

  const handleTaxIdRemove = useCallback(async () => {
    removeTaxId();
  }, [removeTaxId]);

  const columns = useMemo(
    () => [
      {
        Header: 'Month',
        accessor: 'billingPeriod',
        style: {
          fontFamily: 'Inter Bold, sans-serif',
        },
      },
      {
        Header: 'Plan',
        accessor: 'plan',
      },
      {
        Header: 'Status',
        accessor: 'status',
      },
      {
        Header: 'View',
        accessor: 'view',
      },
      {
        Header: 'Download',
        accessor: 'download',
      },
      {
        Header: '',
        accessor: 'moreInfo',
      },
    ],
    []
  );

  const fetchInvoices = useCallback(async ({ pageIndex, pageSize }) => {
    const fetchId = ++fetchIdRef.current;

    setLoading(true);

    setTimeout(async () => {
      if (fetchId === fetchIdRef.current) {
        try {
          const currentPage = pageIndex + 1;
          const response = await authAxiosRef.current.get(
            `/internal/v1/billing/invoices?page=${currentPage}&perPage=${pageSize}`
          );

          const items = Array.isArray(response?.data?.data)
            ? response.data.data
            : [];
          const totalFromApi = response?.data?.meta?.totalCount ?? items.length;
          const pagesFromApi = response?.data?.meta?.totalPages;

          setInvoices(items);
          setPageCount(
            Math.max(1, pagesFromApi ?? Math.ceil(totalFromApi / pageSize))
          );
          setLoading(false);
        } catch (error) {
          console.log(error);
          setInvoices([]);
          setPageCount(1);
          setLoading(false);
        }
      }
    }, 0);
  }, []);

  useEffect(() => {
    fetchInvoices({ pageSize: 10, pageIndex: 0 });
  }, []);

  return (
    <>
      <Grid
        templateColumns={{
          base: '1fr',
          lg: '2fr 1fr',
        }}
        gap={{
          base: 0,
          lg: 4,
        }}
        w="100%"
      >
        <Box bg="white" borderRadius="15px" borderWidth="1px" py={6} px={8}>
          <Text
            textStyle="bodyFamilyRegular"
            fontSize="14px"
            color="orangePrimary"
          >
            Manage your payments
          </Text>
          <Text textStyle="heading3" mt="10px">
            Payment methods
          </Text>
          <Divider mt="10px" border="1px solid lightGrey" />
          <PaymentMethodsList
            paymentMethods={paymentMethods}
            isLoading={isLoading || isUpdatingPayment}
            onAddPaymentMethod={handleAddPaymentMethod}
            onSetDefault={handleSetDefaultConfirm}
            onRemove={handleRemoveConfirm}
          />
        </Box>
        <Flex
          direction="column"
          gap={4}
          height="100%"
          mt={{ base: '20px', md: '0' }}
        >
          <Box
            py={6}
            px={8}
            bg="white"
            borderRadius="15px"
            borderWidth="1px"
            height="100%"
          >
            <Text
              textStyle="bodyFamilyRegular"
              fontSize="14px"
              color="orangePrimary"
            >
              Next payment
            </Text>
            <Text textStyle="heading3" mt="10px">
              {subscription?.status === 'canceled'
                ? '-'
                : formatPrice(subscription?.plan?.amount)}
            </Text>
            <Divider mt="10px" border="1px solid lightGrey" />
            <Flex direction="row" justifyContent="space-between" mt="20px">
              <Text textStyle="bodyFamilyRegular" fontSize="14px">
                {subscription?.status === 'canceled'
                  ? '-'
                  : moment(subscription?.nextBillingDate).format('DD MMMM YYYY')}
              </Text>
              <Text textStyle="bodyFamilyMedium" fontSize="14px">
                {subscription?.status === 'canceled'
                  ? '-'
                  : `${subscription?.plan?.name} plan`}
              </Text>
            </Flex>
          </Box>
          <TaxIdDisplay
            taxId={taxId}
            isLoading={isLoading}
            onEdit={handleTaxIdEdit}
          />
        </Flex>
      </Grid>
      <Box
        mt={{ base: '35px', md: '20px' }}
        p={8}
        bg="white"
        borderRadius="15px"
        borderWidth="1px"
      >
        <Flex
          direction={{
            base: 'column',
            md: 'row',
          }}
          justifyContent="space-between"
          align={{
            base: 'flex-start',
            md: 'center',
          }}
        >
          <Text textStyle="heading3">Billing details</Text>
          <Link
            textStyle="bodyFamilyRegular"
            fontSize="14px"
            textDecoration="underline"
            _hover={{
              color: 'orangePrimary',
            }}
            onClick={onContactModalOpen}
          >
            Edit details
          </Link>
        </Flex>
        <Divider mt="10px" border="1px solid lightGrey" />
        <Grid
          templateColumns={{
            base: '1fr',
            md: '1fr 1fr',
          }}
          gap={4}
          mt="35px"
        >
          <Flex direction="column" gap={{ base: 4, md: 8 }}>
            <Flex
              direction={{
                base: 'column',
                md: 'row',
              }}
              gap={2}
            >
              <Text textStyle="bodyFamilyMedium" fontSize="16px">
                Organisation name:{' '}
              </Text>
              <Text textStyle="bodyFamilyRegular" fontSize="16px">
                {customer?.name}
              </Text>
            </Flex>
            <Flex
              direction={{
                base: 'column',
                md: 'row',
              }}
              gap={2}
            >
              <Text textStyle="bodyFamilyMedium" fontSize="16px">
                E-mail:{' '}
              </Text>
              <Text textStyle="bodyFamilyRegular" fontSize="16px">
                {customer?.email}
              </Text>
            </Flex>
          </Flex>
          <Flex direction="column" gap={{ base: 4, md: 8 }}>
            <Flex
              direction={{
                base: 'column',
                md: 'row',
              }}
              gap={2}
            >
              <Text textStyle="bodyFamilyMedium" fontSize="16px">
                Contact number:{' '}
              </Text>
              <Text textStyle="bodyFamilyRegular" fontSize="16px">
                {customer?.phone}
              </Text>
            </Flex>
            <Flex
              direction={{
                base: 'column',
                md: 'row',
              }}
              gap={2}
            >
              <Text textStyle="bodyFamilyMedium" fontSize="16px">
                Address:
              </Text>
              <Text textStyle="bodyFamilyRegular" fontSize="16px">
                <Text as="span" textStyle="bodyFamilyRegular" fontSize="16px">
                  {customer?.address?.line1}
                </Text>
                {customer?.address?.line2 && (
                  <Text as="span" textStyle="bodyFamilyRegular" fontSize="16px">
                    , {customer?.address?.line2}
                  </Text>
                )}
                {customer?.address?.city && (
                  <Text as="span" textStyle="bodyFamilyRegular" fontSize="16px">
                    , {customer?.address?.city}
                  </Text>
                )}
                {customer?.address?.state && (
                  <Text as="span" textStyle="bodyFamilyRegular" fontSize="16px">
                    , {customer?.address?.state}
                  </Text>
                )}
                {customer?.address?.postal_code && (
                  <Text as="span" textStyle="bodyFamilyRegular" fontSize="16px">
                    , {customer?.address?.postal_code}
                  </Text>
                )}
                {customer?.address?.country && (
                  <Text as="span" textStyle="bodyFamilyRegular" fontSize="16px">
                    , {customer?.address?.country}
                  </Text>
                )}
              </Text>
            </Flex>
          </Flex>
        </Grid>
        <Flex direction="row" gap={1} w="100%" mt="50px" align="center">
          <WarningIcon
            color="#989898"
            w={['10px', '12px']}
            h={['10px', '12px']}
          />
          <Text
            color="#989898"
            fontSize={['12px', '14px']}
            textStyle="bodyFamilyRegular"
            mt={0.5}
          >
            This is the information that will be displayed on your invoice.
          </Text>
        </Flex>
      </Box>
      <Box mt="20px" p={8} bg="white" borderRadius="15px" borderWidth="1px">
        <Text textStyle="heading3">Invoices</Text>
        <Divider mt="10px" border="1px solid lightGrey" />
        <BillingHistoryTable
          columns={columns}
          data={invoices}
          fetchData={fetchInvoices}
          isLoading={loading}
          pageCount={pageCount}
        />
      </Box>
      {/* Modals */}
      <UpdateBillingContactModal
        isOpen={isContactModalOpen}
        onClose={onContactModalClose}
        currentBillingContact={customer}
        onSubmit={handleUpdateBillingContact}
      />

      <AddPaymentMethodModal
        isOpen={isPaymentModalOpen}
        onClose={onPaymentModalClose}
        onSubmit={handlePaymentSubmit}
        createSetupIntent={createSetupIntent}
        setError={setPaymentMethodError}
      />

      <SetDefaultPaymentMethodModal
        isOpen={isSetDefaultModalOpen}
        onClose={onSetDefaultModalClose}
        paymentMethod={selectedPaymentMethod}
        onConfirm={handleSetDefaultPaymentMethod}
        isLoading={isUpdatingPayment}
      />

      <RemovePaymentMethodModal
        isOpen={isRemoveModalOpen}
        onClose={onRemoveModalClose}
        paymentMethod={selectedPaymentMethod}
        onConfirm={handleRemovePaymentMethod}
        isLoading={isUpdatingPayment}
      />

      <TaxIdEditModal
        isOpen={isTaxIdModalOpen}
        onClose={onTaxIdModalClose}
        currentTaxId={taxId}
        onSubmit={handleTaxIdUpdate}
        onRemove={handleTaxIdRemove}
      />
    </>
  );
};

export default BillingAndPayment;
